import { useEffect, useState, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AlertRuleCreation,
    SimulationFeed,
    useSimulation,
    type SimulationProps,
} from '@/features/AlertRuleCreation';
import { BackButton } from '@/components';
import { SidePanel } from '@/components/SidePanel';
import { EngineService } from '@/services/engine';
import { Spinner } from '@/components/Spinner';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { FullAlertRule } from '@/types';
import { useDocumentTitle } from '@/hooks';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';

export default function AlertRuleCreatePage() {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const isEditMode = !!id;
    const duplicateFrom = (
        location.state as { duplicateFrom?: FullAlertRule } | null
    )?.duplicateFrom;

    const [alertRule, setAlertRule] = useState<FullAlertRule | undefined>();
    const [isLoading, setIsLoading] = useState(isEditMode);

    useEffect(() => {
        if (!isEditMode || !id) return;
        let cancelled = false;
        setIsLoading(true);
        setAlertRule(undefined);
        void EngineService.getAlertRule(id)
            .then((rule) => {
                if (!cancelled) setAlertRule(rule);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, isEditMode]);

    const {
        simulationEvents,
        isSimulating,
        simulationSteps,
        runSimulation,
        stopSimulation,
    } = useSimulation(t);

    const hasSimulated =
        isSimulating ||
        simulationEvents.length > 0 ||
        simulationSteps.length > 0;

    const [confidenceThreshold, setConfidenceThreshold] = useState<
        number | undefined
    >();
    const [previewConfidenceThreshold, setPreviewConfidenceThreshold] =
        useState<number | null>(null);

    const simulation = useMemo<SimulationProps>(
        () => ({
            run: runSimulation,
            stop: stopSimulation,
            isRunning: isSimulating,
            onConfidenceThresholdChange: setConfidenceThreshold,
            onPreviewConfidenceThreshold: setPreviewConfidenceThreshold,
        }),
        [runSimulation, stopSimulation, isSimulating],
    );

    useDocumentTitle(
        isEditMode ? 'alertRuleEdit' : 'alertRuleCreate',
        isEditMode ? getAlertRuleDisplayName(alertRule) : undefined,
    );

    if (isEditMode && isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="h-full relative">
            <BackButton variant="floating" />
            <div className="h-full overflow-y-auto scrollbar-grey-600 px-4 panels:px-8">
                <div className="w-full max-w-content mx-auto py-8">
                    <h1 className="text-h4 text-white-100 mb-8">
                        {isEditMode
                            ? t('pageTitle.edit')
                            : t('pageTitle.create')}
                    </h1>
                    <AlertRuleCreation
                        key={id ?? 'create'}
                        alertRuleId={id}
                        initialData={isEditMode ? alertRule : duplicateFrom}
                        simulation={simulation}
                    />
                </div>
            </div>
            {hasSimulated && (
                <SidePanel side="right" align="stretch">
                    <SimulationFeed
                        events={simulationEvents}
                        isLoading={isSimulating}
                        confidenceThreshold={
                            previewConfidenceThreshold !== null
                                ? previewConfidenceThreshold
                                : confidenceThreshold
                        }
                        isPreviewThreshold={previewConfidenceThreshold !== null}
                        simulationSteps={simulationSteps}
                    />
                </SidePanel>
            )}
        </div>
    );
}
